import React from 'react';
import { Helmet } from 'react-helmet';
import { publicAsset } from '@/utils/publicAsset';

const AboutUs: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>About Our Expert Team - Social Work Test Prep Academy | LCSW & Software Engineer Founders</title>
        <meta name="description" content="Meet our founding team: a licensed clinical social worker and Board Approved Clinical Supervisor who graduated from the University of Michigan, and an enterprise software engineer. Clinical judgment plus modern technology, built for ASWB exam preparation." />
        <meta property="og:title" content="Expert-Founded ASWB Prep - Meet Our LCSW & Tech Team" />
        <meta property="og:description" content="Founded by an LCSW and Board Approved Clinical Supervisor from the University of Michigan, with an enterprise software engineer. See the professional expertise behind our AI-powered ASWB exam platform." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.socialworktestprepacademy.com/about" />
        <meta property="og:image" content="https://www.socialworktestprepacademy.com/images/about-og-image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="Social Work Test Prep Academy" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Expert Team - LCSW & Tech Founders" />
        <meta name="twitter:description" content="Founded by an LCSW and Board Approved Clinical Supervisor from the University of Michigan, with an enterprise software engineer. Professional expertise behind AI-powered ASWB prep." />
        <meta name="twitter:image" content="https://www.socialworktestprepacademy.com/images/about-og-image.png" />
        {/* Clean canonical — NOT window.location.href, which folds UTM/ref into
            the canonical and self-competes every campaign link (SEO audit). */}
        <link rel="canonical" href="https://www.socialworktestprepacademy.com/about" />
      </Helmet>
      <div className="min-h-screen bg-neutral-50">
        <div className="container-base py-12 md:py-20">
          <h1 className="display-2 heading-1 text-5xl md:text-6xl text-neutral-900 mb-12 text-center">ABOUT US</h1>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <CoFounderSection
              name="Jordan Coin Jackson"
              title="Co Founder"
              imageSrc={publicAsset('jordan-image.png')}
              description="Our co-founder is a self-made corporate software engineer with a passion for using technology to make a positive impact. With a deep understanding of the challenges faced by social workers, having dedicated their skills and experience to creating a platform that supports social workers in their professional growth journey."
              quote="My goal is to empower social workers by providing accessible, high-quality test preparation resources, ensuring every social worker has the tools they need to succeed. Driven by a commitment to service, I believe in the power of education and technology to transform lives and communities."
            />
            
            <CoFounderSection
              name="Pamela Coin, LCSW"
              title="Co-founder · Board Approved Clinical Supervisor"
              imageSrc={publicAsset('pamela-image.png')}
              description="Our co-founder is a proud University of Michigan graduate, a licensed clinical social worker, and a Board Approved Clinical Supervisor practicing in Washington, D.C."
              quote="I understand social workers' hurdles, especially regarding licensure. Frustrated by the lack of legislative support for change, I have set out to make a difference by helping social workers succeed on their exams. I have played a key role in editing, creating, and reviewing content on our platform, ensuring it is thorough and practical. The questions are AI-assisted, curated with statistical references to varying p-value difficulty levels. While I am not formally trained in psychometrics, I am dedicated to providing social workers with the tools they need to overcome challenges and reach their professional goals."
            />
          </div>
        </div>
      </div>
    </>
  );
};

interface CoFounderSectionProps {
  name: string;
  title: string;
  imageSrc: string;
  description: string;
  quote?: string; // Make quote optional
}

const CoFounderSection: React.FC<CoFounderSectionProps> = ({ name, title, imageSrc, description, quote }) => {
  return (
    <div className="bg-white p-6 rounded-2xl border-2 border-neutral-200 shadow-ledge">
      <div className="aspect-w-16 aspect-h-9 mb-4">
        <img
          src={imageSrc}
          alt={name}
          loading="lazy"
          decoding="async"
          width={640}
          height={360}
          className="w-full h-full object-cover rounded-xl"
        />
      </div>
      <h2 className="heading-2 text-2xl mb-2 text-neutral-900">{name}</h2>
      <h3 className="font-display text-xl text-primary-700 mb-4">{title}</h3>
      <div className="text-neutral-700">
        <p className="body-base">{description}</p>
        {quote && (
          <blockquote className="border-l-4 border-primary-500 pl-4 my-4 italic text-neutral-700">
            {quote}
          </blockquote>
        )}
      </div>
    </div>
  );
};

export default AboutUs;
